import { ReactNode, useCallback, useMemo, useState } from 'react';
import cn from 'classnames';
import { Form } from 'react-bootstrap';
import {
  MdCheckCircle,
  MdInfoOutline,
  MdLink,
  MdOpenInNew,
  MdUploadFile,
  MdWarning
} from 'react-icons/md';
import { ColumnFilterPopover } from '@Components/ColumnFilterPopover';
import { DownloadDropdown } from '@Components/DownloadDropdown';
import { Size } from '@Components/designConstants';
import { TableEmptyState } from '@Components/TableEmptyState';
import { useSetError } from '@src/redux/modals';
import { exportMultiSheetToExcel } from '@src/utils/download-utils';
import { searchItems } from '@src/utils/search';
import { fetchCampaignBreakout } from '../api/data-qa-api';
import {
  buildCampaignBreakoutSheets,
  ExportWindow,
  exportWindowKey,
  modelExportWindow
} from '../campaign-breakout-export-utils';
import { SectionHeader } from '../components/SectionHeader';
import {
  CampaignBreakoutRow,
  CampaignLabelingResolution,
  CampaignLabelingRow,
  ConfigSummary,
  DataQaDataset,
  MmmConfig
} from '../data-qa-types';
import { useAllModelsExport } from '../hooks/useAllModelsExport';
import {
  distinctColumnValues,
  emptyFacetSelection,
  FacetSelection,
  FilterableColumn,
  filterRowsByFacets,
  RESOLUTION_LABEL
} from './campaigns-filter-utils';

interface CampaignsSectionProps {
  config: MmmConfig;
  dataset: DataQaDataset;
  // Redshift table slug -- the export's endpoint param and file/sheet names.
  company: string;
  // Every model config for the company -- the export covers all models.
  configs: ConfigSummary[];
}

type CardTone = 'warning' | 'info' | 'success';

const TONE_ICON: Record<CardTone, ReactNode> = {
  warning: <MdWarning />,
  info: <MdInfoOutline />,
  success: <MdCheckCircle />
};

const moneyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
});
const intFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

// campaignLabeling.blocking/.proactive rows carry a free-text `source` ("Cross Channel"
// or "Offline Inputs" today). Grouped rather than left as just another table column so
// the two are never visually interleaved, and matched by substring (not equality) so a
// minor label change on the backend doesn't silently drop a whole group into "other".
type SourceGroupKey = 'crossChannel' | 'offline' | 'other';

const classifySource = (source: string): SourceGroupKey => {
  const s = source.toLowerCase();
  if (s.includes('cross channel')) return 'crossChannel';
  if (s.includes('offline')) return 'offline';
  return 'other';
};

// idColumnLabel/nameColumnLabel drive the same two columns' header text per group --
// Offline Inputs media tables have no campaign concept (see CampaignLabelingRow's own
// recordId/recordLabel naming), so its group shows "Table ID"/"Table Name" over the
// exact same fields Cross Channel labels "Campaign ID"/"Campaign". recordNoun drives
// the group header's own "N of M <noun>s" count.
const SOURCE_GROUP_META: Record<
  SourceGroupKey,
  {
    label: string;
    icon: ReactNode;
    idColumnLabel: string;
    nameColumnLabel: string;
    recordNoun: string;
  }
> = {
  crossChannel: {
    label: 'Cross-Channel',
    icon: <MdLink />,
    idColumnLabel: 'Campaign ID',
    nameColumnLabel: 'Campaign',
    recordNoun: 'campaign'
  },
  offline: {
    label: 'Offline Inputs',
    icon: <MdUploadFile />,
    idColumnLabel: 'Table ID',
    nameColumnLabel: 'Table Name',
    recordNoun: 'table'
  },
  other: {
    label: 'Other Sources',
    icon: <MdInfoOutline />,
    idColumnLabel: 'Record ID',
    nameColumnLabel: 'Record',
    recordNoun: 'record'
  }
};

// Stable display order — Cross Channel first, then Offline Inputs, then anything
// unrecognized rather than dropping it.
const SOURCE_GROUP_ORDER: SourceGroupKey[] = ['crossChannel', 'offline', 'other'];

// Where a "needs labeling" row resolves, per source. Cross Channel goes to
// Segmentation Mapping, the same tool used everywhere else in this app. Offline
// Inputs' channel/platform/tactic/brand is instead assigned directly on the media
// table via its own admin page (Sean, 2026-08-28) -- "needs featurization" always
// resolves to the same Feature Mapping URL regardless of source (Sean's explicit call).
const resolveLabelingUrl = (groupKey: SourceGroupKey, client: string): string =>
  groupKey === 'offline'
    ? `https://blisspoint.tinuiti.com/${client}/rmmm/offline-inputs`
    : `https://blisspoint.tinuiti.com/${client}/segmentation-mapping/labeling`;

interface SourceGroup {
  key: SourceGroupKey;
  rows: CampaignLabelingRow[];
}

const groupRowsBySource = (rows: CampaignLabelingRow[]): SourceGroup[] => {
  const byKey = new Map<SourceGroupKey, CampaignLabelingRow[]>();
  rows.forEach(row => {
    const key = classifySource(row.source);
    byKey.set(key, [...(byKey.get(key) ?? []), row]);
  });
  return SOURCE_GROUP_ORDER.map(key => ({ key, rows: byKey.get(key) ?? [] })).filter(
    group => group.rows.length > 0
  );
};

const LabelingCard = ({
  tone,
  title,
  children
}: {
  tone: CardTone;
  title: string;
  children: ReactNode;
}) => (
  <div className={`data-qa-labeling-card data-qa-labeling-card--${tone}`}>
    <div className='data-qa-labeling-card__head'>
      <span className='data-qa-labeling-card__icon'>{TONE_ICON[tone]}</span>
      <span className='data-qa-labeling-card__title'>{title}</span>
    </div>
    <div className='data-qa-labeling-card__body'>{children}</div>
  </div>
);

const COLUMN_LABEL: Record<FilterableColumn, string> = {
  channel: 'Channel',
  platform: 'Platform',
  missing: 'Missing',
  resolution: 'Resolution',
  firstSeen: 'First Seen',
  lastSeen: 'Last Seen'
};

// Needs Labeling reads as a warning (nothing at all is resolved yet); Needs
// Featurization as informational (already correctly segmented, just needs a feature).
const ResolutionBadge = ({ resolution }: { resolution: CampaignLabelingRow['resolution'] }) => (
  <span
    className={cn('data-qa-resolution-badge', {
      'data-qa-resolution-badge--label': resolution === CampaignLabelingResolution.LABEL,
      'data-qa-resolution-badge--featurize': resolution === CampaignLabelingResolution.FEATURIZE
    })}
  >
    {RESOLUTION_LABEL[resolution]}
  </span>
);

// The badge plus a small link-out icon to wherever this specific row resolves --
// Segmentation Mapping for a labeling gap, Feature Mapping for a featurization gap.
// Replaces the old group-header link list, which named only one resolve target per
// group even though a group's rows can need either (or both).
const ResolutionCell = ({
  resolution,
  labelingUrl,
  featureMappingUrl
}: {
  resolution: CampaignLabelingRow['resolution'];
  labelingUrl: string;
  featureMappingUrl: string;
}) => {
  const url = resolution === CampaignLabelingResolution.LABEL ? labelingUrl : featureMappingUrl;
  return (
    <div className='data-qa-resolution-cell'>
      <ResolutionBadge resolution={resolution} />
      <a
        className='data-qa-resolution-cell__link'
        href={url}
        target='_blank'
        rel='noreferrer'
        aria-label={`Resolve at ${url}`}
        title={url}
      >
        <MdOpenInNew />
      </a>
    </div>
  );
};

// Wraps the label in the same col-header flex column every other header uses (even
// though this one has no second row below it) so every <th>'s label sits at an
// identical top offset -- a bare <span> here renders its label at a different vertical
// position than a FacetColumnHeader/TextFilterColumnHeader's label, misaligning the
// header row.
const PlainColumnHeader = ({ label }: { label: string }) => (
  <th>
    <div className='data-qa-labeling-table__col-header'>
      <span className='data-qa-labeling-table__col-label'>{label}</span>
    </div>
  </th>
);

const FacetColumnHeader = ({
  column,
  rows,
  selection,
  onFacetChange
}: {
  column: FilterableColumn;
  rows: CampaignLabelingRow[];
  selection: FacetSelection;
  onFacetChange: (column: FilterableColumn, next: ReadonlySet<string>) => void;
}) => (
  <th>
    <div className='data-qa-labeling-table__col-header'>
      <span className='data-qa-labeling-table__col-label'>{COLUMN_LABEL[column]}</span>
      <ColumnFilterPopover
        label={COLUMN_LABEL[column]}
        options={distinctColumnValues(rows, column)}
        selected={selection[column]}
        onChange={next => onFacetChange(column, next)}
      />
    </div>
  </th>
);

const TextFilterColumnHeader = ({
  label,
  placeholder,
  query,
  onQueryChange
}: {
  label: string;
  placeholder: string;
  query: string;
  onQueryChange: (query: string) => void;
}) => (
  <th>
    <div className='data-qa-labeling-table__col-header'>
      <span className='data-qa-labeling-table__col-label'>{label}</span>
      <Form.Control
        className='data-qa-labeling-table__col-filter'
        type='search'
        size='sm'
        placeholder={placeholder}
        value={query}
        onChange={e => onQueryChange(e.target.value)}
      />
    </div>
  </th>
);

const TABLE_COLUMN_COUNT = 10;

// One source's rows, in their own clearly separated card: a header naming the source
// (with how many records) and a table scoped to just that source's columns — Source
// itself is omitted here since every row in the table already shares it via the header
// above. Where to resolve each row lives on its own Resolution cell (see
// ResolutionCell) rather than a group-level link list, since a group's rows can need
// either labeling or featurization, and each source resolves "needs labeling" to a
// different place (see resolveLabelingUrl). Filtering state (column facets + the
// record-id/record-label text search) is local to this one group, not shared across
// sources — narrowing Cross Channel's Missing column has no bearing on Offline Inputs'
// unrelated rows.
const SourceGroupTable = ({
  group,
  client,
  featureMappingUrl
}: {
  group: SourceGroup;
  client: string;
  featureMappingUrl: string;
}) => {
  const { label, icon, idColumnLabel, nameColumnLabel, recordNoun } = SOURCE_GROUP_META[group.key];
  const labelingUrl = resolveLabelingUrl(group.key, client);
  const [facetSelection, setFacetSelection] = useState<FacetSelection>(emptyFacetSelection());
  const [recordIdQuery, setRecordIdQuery] = useState('');
  const [recordLabelQuery, setRecordLabelQuery] = useState('');

  const handleFacetChange = (column: FilterableColumn, next: ReadonlySet<string>) => {
    setFacetSelection(prev => ({ ...prev, [column]: next }));
  };

  const visibleRows = useMemo(() => {
    const byFacet = filterRowsByFacets(group.rows, facetSelection);
    const byRecordId = searchItems(byFacet, recordIdQuery, {
      keys: ['recordId'],
      rank: false
    });
    return searchItems(byRecordId, recordLabelQuery, { keys: ['recordLabel'], rank: false });
  }, [group.rows, facetSelection, recordIdQuery, recordLabelQuery]);

  return (
    <div className='data-qa-source-group'>
      <div className='data-qa-source-group__header'>
        <span className='data-qa-source-group__icon'>{icon}</span>
        <span className='data-qa-source-group__title'>{label}</span>
        <span className='data-qa-source-group__count'>
          {visibleRows.length} of {group.rows.length} {recordNoun}
          {group.rows.length === 1 ? '' : 's'}
        </span>
      </div>
      <table className='data-qa-labeling-table'>
        <colgroup>
          <col className='data-qa-labeling-table__col--channel' />
          <col className='data-qa-labeling-table__col--platform' />
          <col className='data-qa-labeling-table__col--record-id' />
          <col className='data-qa-labeling-table__col--record-label' />
          <col className='data-qa-labeling-table__col--missing' />
          <col className='data-qa-labeling-table__col--resolution' />
          <col className='data-qa-labeling-table__col--date' />
          <col className='data-qa-labeling-table__col--date' />
          <col className='data-qa-labeling-table__col--metric' />
          <col className='data-qa-labeling-table__col--metric' />
        </colgroup>
        <thead>
          <tr>
            <FacetColumnHeader
              column='channel'
              rows={group.rows}
              selection={facetSelection}
              onFacetChange={handleFacetChange}
            />
            <FacetColumnHeader
              column='platform'
              rows={group.rows}
              selection={facetSelection}
              onFacetChange={handleFacetChange}
            />
            <TextFilterColumnHeader
              label={idColumnLabel}
              placeholder={`Filter ${idColumnLabel.toLowerCase()}...`}
              query={recordIdQuery}
              onQueryChange={setRecordIdQuery}
            />
            <TextFilterColumnHeader
              label={nameColumnLabel}
              placeholder={`Filter ${nameColumnLabel.toLowerCase()}...`}
              query={recordLabelQuery}
              onQueryChange={setRecordLabelQuery}
            />
            <FacetColumnHeader
              column='missing'
              rows={group.rows}
              selection={facetSelection}
              onFacetChange={handleFacetChange}
            />
            <FacetColumnHeader
              column='resolution'
              rows={group.rows}
              selection={facetSelection}
              onFacetChange={handleFacetChange}
            />
            <FacetColumnHeader
              column='firstSeen'
              rows={group.rows}
              selection={facetSelection}
              onFacetChange={handleFacetChange}
            />
            <FacetColumnHeader
              column='lastSeen'
              rows={group.rows}
              selection={facetSelection}
              onFacetChange={handleFacetChange}
            />
            <PlainColumnHeader label='Spend' />
            <PlainColumnHeader label='Impressions' />
          </tr>
        </thead>
        <tbody>
          {visibleRows.length === 0 ? (
            <tr>
              <td colSpan={TABLE_COLUMN_COUNT}>
                <TableEmptyState message={`No ${recordNoun}s match the current filters.`} />
              </td>
            </tr>
          ) : (
            visibleRows.map(r => (
              <tr key={`${r.channel}|${r.platform}|${r.recordId}|${r.recordLabel}`}>
                <td>{r.channel}</td>
                <td>{r.platform}</td>
                <td>{r.recordId}</td>
                <td>
                  <span className='data-qa-labeling-table__record-label' title={r.recordLabel}>
                    {r.recordLabel}
                  </span>
                </td>
                <td>{r.missing.join(', ')}</td>
                <td>
                  <ResolutionCell
                    resolution={r.resolution}
                    labelingUrl={labelingUrl}
                    featureMappingUrl={featureMappingUrl}
                  />
                </td>
                <td className='data-qa-labeling-table__date-cell'>{r.firstSeen}</td>
                <td className='data-qa-labeling-table__date-cell'>{r.lastSeen}</td>
                <td>{r.spend === undefined ? '' : moneyFormatter.format(r.spend)}</td>
                <td>{r.impressions === undefined ? '' : intFormatter.format(r.impressions)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

const SourceGroupTables = ({
  rows,
  client,
  featureMappingUrl,
  configPath
}: {
  rows: CampaignLabelingRow[];
  client: string;
  featureMappingUrl: string;
  configPath: string;
}) => (
  <>
    {groupRowsBySource(rows).map(group => (
      // Keyed on configPath too, not just group.key -- this component stays mounted
      // across a ModelSwitcher model change (CampaignsSection has no key tied to the
      // active config), so without this its local facetSelection/recordIdQuery/
      // recordLabelQuery state would silently carry over from the previous model and
      // can zero out the new model's table with no reset or warning. Including
      // configPath in the key forces a remount (and fresh local state) on switch.
      <SourceGroupTable
        key={`${group.key}-${configPath}`}
        group={group}
        client={client}
        featureMappingUrl={featureMappingUrl}
      />
    ))}
  </>
);

export const CampaignsSection = ({ config, dataset, company, configs }: CampaignsSectionProps) => {
  const labeling = dataset.campaignLabeling;

  const { models: exportModels, unavailable: exportUnavailable } = useAllModelsExport(
    company,
    configs,
    'Campaign export'
  );
  const [exporting, setExporting] = useState(false);
  const setError = useSetError();
  // Fetched on click rather than with the model load: it's a company-wide campaign list
  // most sessions never need. Not cached -- campaign delivery changes daily, so a second
  // download in the same session should be current. One request per distinct window.
  const handleExportExcel = useCallback(async () => {
    setExporting(true);
    try {
      const windows = new Map<string, ExportWindow>();
      exportModels.forEach(model => {
        const exportWindow = modelExportWindow(model);
        if (exportWindow) {
          windows.set(exportWindowKey(exportWindow), exportWindow);
        }
      });
      const rowsByWindow = new Map<string, CampaignBreakoutRow[]>();
      await Promise.all(
        [...windows].map(async ([key, { startDate, endDate }]) => {
          rowsByWindow.set(key, await fetchCampaignBreakout(company, startDate, endDate));
        })
      );
      const sheets = buildCampaignBreakoutSheets(company, exportModels, rowsByWindow);
      await exportMultiSheetToExcel(sheets, `${company}_campaign_export`);
    } catch (e) {
      setError({
        title: "Couldn't export campaigns",
        message: `Campaign export failed: ${e.message}`,
        reportError: e
      });
    } finally {
      setExporting(false);
    }
  }, [company, exportModels, setError]);
  const client = config.clientName;
  const labelingUrl = `https://blisspoint.tinuiti.com/${client}/segmentation-mapping/labeling`;
  const featureMappingUrl = `https://blisspoint.tinuiti.com/${client}/mmm/feature-mapping`;

  const header = (
    <SectionHeader
      title='Campaign Review'
      subtitle='Model features are constituted from campaign taxonomy. Before modeling can proceed, all segments used in model featurization must be applied to all campaigns, and all labeled campaigns must be featurized.'
      tips={
        <>
          <li>
            <strong>Only unmapped or un-featurized campaigns show up here</strong> — a clean list
            means every campaign is mapped to a real feature
          </li>
          <li>
            <strong>Blocking</strong>: campaigns that delivered inside the model window must be
            resolved before review can proceed
          </li>
          <li>
            <strong>Proactive</strong>: campaigns that only delivered outside the window won&apos;t
            affect this run, but it&apos;s best practice to resolve them for future refreshes
          </li>
        </>
      }
    />
  );

  const renderBody = () => {
    if (labeling.availability === 'error') {
      return (
        <LabelingCard tone='warning' title='Campaign Taxonomy Labeling'>
          <div>
            {labeling.message ??
              'Campaign labeling validation is temporarily unavailable — verify manually in the meantime.'}
          </div>
          <div className='data-qa-labeling-card__links'>
            Verify labeling at{' '}
            <a href={labelingUrl} target='_blank' rel='noreferrer'>
              {labelingUrl}
            </a>
            .
          </div>
        </LabelingCard>
      );
    }

    if (labeling.availability === 'unavailable') {
      return (
        <LabelingCard tone='info' title='Campaign Taxonomy Labeling'>
          <div>
            {labeling.message ??
              'Automated validation is not yet available for this client. Please manually verify that all campaigns are properly labeled.'}
          </div>
          <div className='data-qa-labeling-card__links'>
            Verify at{' '}
            <a href={labelingUrl} target='_blank' rel='noreferrer'>
              {labelingUrl}
            </a>
            .
          </div>
        </LabelingCard>
      );
    }

    const { blocking, proactive } = labeling;

    return (
      <>
        {blocking.length === 0 ? (
          <LabelingCard tone='success' title='Campaign Taxonomy Labeling'>
            <div>
              {proactive.length === 0
                ? 'All relevant campaigns are properly labeled.'
                : 'All campaigns affecting this model run are properly labeled.'}
            </div>
          </LabelingCard>
        ) : (
          <>
            <LabelingCard tone='warning' title='Campaign Taxonomy Labeling'>
              <div className='data-qa-labeling-card__lead'>
                {blocking.length} campaign{blocking.length === 1 ? '' : 's'}/table
                {blocking.length === 1 ? '' : 's'} {blocking.length === 1 ? 'needs' : 'need'}{' '}
                labeling or featurization before review can proceed.
              </div>
            </LabelingCard>
            <SourceGroupTables
              rows={blocking}
              client={client}
              featureMappingUrl={featureMappingUrl}
              configPath={config.configPath}
            />
          </>
        )}

        {proactive.length > 0 && (
          <>
            <LabelingCard tone='info' title='Proactive Resolution for Future Refreshes'>
              <div className='data-qa-labeling-card__lead'>
                {proactive.length} unresolved campaign{proactive.length === 1 ? '' : 's'}/table
                {proactive.length === 1 ? '' : 's'} only delivered outside the model window. They
                won&apos;t impact this model run, but please label or featurize them proactively to
                keep future refreshes smooth.
              </div>
            </LabelingCard>
            <SourceGroupTables
              rows={proactive}
              client={client}
              featureMappingUrl={featureMappingUrl}
              configPath={config.configPath}
            />
          </>
        )}
      </>
    );
  };

  return (
    <div className='data-qa-campaigns'>
      {header}
      <div className='data-qa-campaigns__toolbar'>
        <DownloadDropdown
          size={Size.SM}
          disabled={exportUnavailable || exporting}
          menuOptions={['XLSX']}
          onClickOptions={[handleExportExcel]}
        />
      </div>
      {renderBody()}
    </div>
  );
};
